/**
 * FileUpload Component for Project Inbox
 *
 * Provides drag-and-drop file upload with preview and progress indication.
 */
import { useState, useCallback, useRef } from 'react'
import {
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  CodeBracketIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline'

interface UploadedFile {
  id: string
  filename: string
  original_filename: string
  mime_type: string
  file_size: number
  storage_path: string
}

interface FileUploadProps {
  channelId: string
  projectId: string
  onUploadComplete: (file: UploadedFile) => void
  onError?: (error: string) => void
  maxFiles?: number
  maxFileSize?: number // in bytes
}

interface FilePreview {
  file: File
  preview?: string
  uploading: boolean
  progress: number
  error?: string
  uploaded?: UploadedFile
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const getFileIcon = (mimeType: string, filename: string) => {
  if (mimeType.startsWith('image/')) {
    return <PhotoIcon className="h-8 w-8 text-purple-500" />
  }

  const codeExtensions = ['.py', '.js', '.ts', '.jsx', '.tsx', '.java', '.c', '.cpp', '.go', '.rs']
  if (codeExtensions.some(ext => filename.toLowerCase().endsWith(ext))) {
    return <CodeBracketIcon className="h-8 w-8 text-green-500" />
  }

  return <DocumentIcon className="h-8 w-8 text-blue-500" />
}

export default function FileUpload({
  channelId,
  projectId,
  onUploadComplete,
  onError,
  maxFiles = 5,
  maxFileSize = 50 * 1024 * 1024, // 50MB default
}: FileUploadProps) {
  const [files, setFiles] = useState<FilePreview[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    handleFiles(droppedFiles)
  }, [])

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files)
      handleFiles(selectedFiles)
    }
  }, [])

  const handleFiles = (newFiles: File[]) => {
    // Limit number of files
    const availableSlots = maxFiles - files.length
    const filesToAdd = newFiles.slice(0, availableSlots)

    const previews: FilePreview[] = filesToAdd.map(file => {
      const preview: FilePreview = {
        file,
        uploading: false,
        progress: 0,
      }

      // Create preview for images
      if (file.type.startsWith('image/')) {
        preview.preview = URL.createObjectURL(file)
      }

      return preview
    })

    setFiles(prev => [...prev, ...previews])

    // Start uploading each file
    previews.forEach((preview, index) => {
      uploadFile(preview.file, files.length + index)
    })
  }

  const uploadFile = async (file: File, fileIndex: number) => {
    // Validate file size
    if (file.size > maxFileSize) {
      setFiles(prev => prev.map((f, i) =>
        i === fileIndex
          ? { ...f, error: `File too large. Maximum: ${formatFileSize(maxFileSize)}` }
          : f
      ))
      onError?.(`File ${file.name} is too large`)
      return
    }

    // Set uploading state
    setFiles(prev => prev.map((f, i) =>
      i === fileIndex ? { ...f, uploading: true, progress: 0 } : f
    ))

    try {
      const formData = new FormData()
      formData.append('file', file)

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest()

      const uploadPromise = new Promise<UploadedFile>((resolve, reject) => {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100)
            setFiles(prev => prev.map((f, i) =>
              i === fileIndex ? { ...f, progress } : f
            ))
          }
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText))
          } else {
            reject(new Error(xhr.statusText || 'Upload failed'))
          }
        }

        xhr.onerror = () => reject(new Error('Network error'))

        const token = localStorage.getItem('access_token')
        xhr.open('POST', `/api/v1/inbox/channels/${channelId}/upload?project_id=${projectId}`)
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`)
        }
        xhr.send(formData)
      })

      const uploaded = await uploadPromise

      setFiles(prev => prev.map((f, i) =>
        i === fileIndex
          ? { ...f, uploading: false, progress: 100, uploaded }
          : f
      ))

      onUploadComplete(uploaded)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed'
      setFiles(prev => prev.map((f, i) =>
        i === fileIndex
          ? { ...f, uploading: false, error: errorMessage }
          : f
      ))
      onError?.(errorMessage)
    }
  }

  const removeFile = (index: number) => {
    const file = files[index]
    if (file.preview) {
      URL.revokeObjectURL(file.preview)
    }
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const clearAll = () => {
    files.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview)
    })
    setFiles([])
  }

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${isDragging
            ? 'border-blue-500 bg-blue-500/10'
            : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInput}
          className="hidden"
        />
        <ArrowUpTrayIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-600">
          Drop files here or <span className="text-blue-600">browse</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Max {maxFiles} files, {formatFileSize(maxFileSize)} each
        </p>
      </div>

      {/* File previews */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {files.length} file{files.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={clearAll}
              className="text-xs text-red-600 hover:text-red-600"
            >
              Clear all
            </button>
          </div>

          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200"
            >
              {/* Preview/Icon */}
              <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                {file.preview ? (
                  <img
                    src={file.preview}
                    alt={file.file.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  getFileIcon(file.file.type, file.file.name)
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {file.file.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(file.file.size)}
                </p>

                {/* Progress bar */}
                {file.uploading && (
                  <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                )}

                {/* Error message */}
                {file.error && (
                  <p className="text-xs text-red-600 mt-1">{file.error}</p>
                )}

                {/* Success indicator */}
                {file.uploaded && (
                  <p className="text-xs text-green-600 mt-1">Uploaded</p>
                )}
              </div>

              {/* Remove button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  removeFile(index)
                }}
                className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export type { UploadedFile }
